import React from 'react';
import { useIntl } from 'react-intl';
import { gql, useQuery } from '@apollo/client';
import Card from '../../../../ui/canon/Card';
import { CheckCircle2, ArrowRight as ArrowRightIcon } from 'lucide-react';
import { getAvatarStyle } from '../../lib/s3';

const GET_CURRENT_USER_PROFILE = gql`
  query GetCurrentUserProfile {
    getCurrentUser {
      id
      name
      headline
      profile_picture
      profile_percentage_complete
      # Shares the profile page's number so an advisor never reads 100% there and
      # 70% here. Both fields are requested so a refetch resolving to this
      # document (they share an operation name) keeps the cache consistent.
      profile_section_percentage
      profile_section_completion {
        key
        complete
        countable
        reason_not_countable
      }
      company {
        id
        avatar_background_color
        avatar_directory
        primary_color
      }
    }
  }
`;

const ProfileCompletionCard = ({ className = '', elevateOnHover = true, compact = false }) => {
  const intl = useIntl();
  const { data, loading, error } = useQuery(GET_CURRENT_USER_PROFILE, {
    fetchPolicy: 'cache-first',
  });

  if (loading) {
    return (
      <Card className={className}>
        <div className={compact ? "p-4" : "px-4 py-5 sm:p-6"}>
          <div className="text-succession-text-secondary text-sm">{intl.formatMessage({ id: 'succession.launchpad.profile_loading' })}</div>
        </div>
      </Card>
    );
  }

  if (error || !data?.getCurrentUser) {
    return null;
  }

  const user = data.getCurrentUser;
  // Page-scoped completion over the sections a user can actually finish, matching
  // the profile page's banner. Falls back to the stored weighted column only if
  // the field is unavailable (e.g. a guard denied it).
  const profilePercentage = user.profile_section_percentage
    ?? user.profile_percentage_complete
    ?? 0;
  const isComplete = profilePercentage >= 100;
  const linkText = isComplete ? intl.formatMessage({ id: 'succession.launchpad.profile_edit' }) : intl.formatMessage({ id: 'succession.launchpad.profile_complete' });
  const linkHref = '/profile/getting_started';

  // Truncate headline to approximately match the image (around 20-25 chars)
  const truncatedHeadline = user.headline
    ? user.headline.length > 22
      ? `${user.headline.substring(0, 22)}...`
      : user.headline
    : '';

  // Get profile picture URL and background color in one call
  const companySettings = user.company || {};
  const avatar = getAvatarStyle(user.profile_picture, companySettings);

  const hoverClasses = elevateOnHover
    ? 'hover:shadow-succession-lg transform hover:scale-105'
    : '';

  // Compact variant - matches GettingStartedProgress styling
  if (compact) {
    return (
      <div
        className={`bg-succession-bg-primary border border-succession-border rounded-lg shadow-succession-md hover:shadow-succession-lg transition-shadow h-full cursor-pointer ${className}`}
        onClick={() => window.location.href = linkHref}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); window.location.href = linkHref; }}}
      >
        {/* Content with padding - matching GettingStartedProgress p-6 */}
        <div className="p-6 h-full flex flex-col">
          {/* Header row */}
          <div className="flex items-start justify-between mb-4 gap-2">
            <div className="flex items-center gap-3 min-w-0 flex-1">
                {/* Avatar */}
                <div 
                className="w-10 h-10 rounded-full overflow-hidden mb-1 flex-shrink-0"
                style={{ backgroundColor: avatar.isNewStyle ? avatar.backgroundColor : 'var(--succession-bg-secondary)' }}
                >
                  <img
                    src={avatar.url}
                    alt={user.name}
                    className="w-full h-full object-cover"
                    style={avatar.isNewStyle ? { objectFit: 'contain', objectPosition: 'bottom center', transform: 'scale(0.85)', transformOrigin: 'bottom center' } : {}}
                  />
                </div>
              <div className="min-w-0 overflow-hidden">
                <h3 className="text-lg font-semibold text-succession-text-primary mb-1 truncate">{user.name}</h3>
                <span className="inline-flex items-center text-sm font-medium text-succession-primary hover:text-succession-primary-hover transition-colors group">
                  {linkText}
                  <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 text-succession-success flex-shrink-0">
              <CheckCircle2 className="w-5 h-5" />
              <span className="text-2xl font-bold">{profilePercentage}{intl.locale === 'fr' ? '\u00a0%' : '%'}</span>
            </div>
          </div>
          {/* Progress bar - pushed to bottom with mt-auto */}
          <div className="mt-auto">
            <div className="h-2 bg-succession-bg-secondary rounded-full overflow-hidden">
              <div
                className="h-full bg-succession-success rounded-full transition-all duration-500 ease-out"
                style={{ width: `${Math.min(100, Math.max(0, profilePercentage))}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default variant - horizontal layout
  return (
    <Card
      className={`transition-all duration-300 ${hoverClasses} ${className}`}
      variant="outlined"
    >
      <div className="px-4 py-5 sm:p-6">
        {/* First row: Avatar and Name */}
        <div className="flex items-start gap-4 mb-3">
          {/* Avatar */}
          <div 
            className="flex-shrink-0 w-16 h-16 rounded-full overflow-hidden"
            style={{ backgroundColor: avatar.isNewStyle ? avatar.backgroundColor : 'var(--succession-bg-secondary)' }}
          >
            <img
              src={avatar.url}
              alt={user.name}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Name and Headline */}
          <div className="flex-1 min-w-0">
            {/* Name */}
            <h3 className="text-succession-text-primary font-semibold mb-0.5">
              {user.name}
            </h3>

            {/* Headline */}
            {truncatedHeadline && (
              <p className="text-succession-text-secondary text-sm">
                {truncatedHeadline}
              </p>
            )}
          </div>
        </div>

        {/* Second row: Link and Progress Bar (spanning full width) */}
        <div>
          {/* Link */}
          <a
            href={linkHref}
            className="text-succession-success hover:text-succession-success-hover text-sm font-medium inline-block mb-3"
          >
            {linkText}
          </a>

          {/* Progress bar container */}
          <div className="flex items-center gap-3">
            {/* Progress bar */}
            <div className="flex-1 h-2 bg-succession-bg-secondary rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-300"
                style={{ 
                  width: `${Math.min(100, Math.max(0, profilePercentage))}%`,
                  backgroundColor: 'var(--succession-primary, #4f46e5)'
                }}
              />
            </div>
            {/* Percentage */}
            <span className="text-succession-text-primary text-sm font-medium flex-shrink-0">
              {profilePercentage}{intl.locale === 'fr' ? '\u00a0%' : '%'}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ProfileCompletionCard;

